import cx from 'classnames';
import {QUESTION_TYPES} from 'client/constants';
import type {RefObject} from 'react';
import React, {useCallback, useContext, useEffect} from 'react';

import {copyToClipboard} from '@albert-io/atomic';
import type {QuestionModelV3} from '@albert-io/models';
import appActions from 'client/AppActions';
import {callAction} from 'client/framework';
import {
  GlobalNavBehaviorContext,
  GlobalNavBehaviorEnum
} from 'client/GlobalNavigation/GlobalNavBehavior.context';
import usePrevious from 'lib/hooks/usePrevious';

import {debounce} from 'lodash';
import './practice-view-wrapper.scss';

/** Spread on the element passed as `mainWrapperRef`. */
export const QUESTION_AREA_PROPS = {
  role: 'region',
  'aria-label': 'Question',
  tabIndex: -1
} as const;

export const QUESTION_NAV_ATTRIBUTE = 'data-question-nav';

/** Spread on Next/Previous controls outside the question area: focus inside them follows to the new question. */
export const QUESTION_NAV_PROPS = {'data-question-nav': ''} as const;

/** Moves focus to the question area when focus was lost, inside it, or on a question nav control. */
const focusQuestionArea = (questionArea: HTMLElement): void => {
  const active = document.activeElement;
  const focusIsAdrift = !active || active === document.body;
  if (
    focusIsAdrift ||
    questionArea.contains(active) ||
    active.closest(`[${QUESTION_NAV_ATTRIBUTE}]`)
  ) {
    questionArea.focus({preventScroll: true});
  }
};

interface PracticeViewWrapperProps extends PropsWithChildrenRequired {
  activeQuestion: QuestionModelV3;
  questionRendererType: 'PreGuess' | 'PostGuess';
  studentId?: string;
  displayOrientation: 'default' | 'vertical' | 'horizontal';
  mainWrapperRef?: RefObject<HTMLDivElement | null>;
  answerInputPaneRef: RefObject<HTMLDivElement | null>;
  stackedContainerRef: RefObject<HTMLDivElement | null>;
  postSubmissionScrollPointRef: RefObject<HTMLDivElement | null>;
  hideUsernameBar?: boolean;
  className?: string;
  /** For hosts that remount the practice view per question: focuses the question area on mount. */
  focusQuestionOnMount?: boolean;
}

export const PracticeViewWrapper = ({
  activeQuestion,
  questionRendererType,
  studentId,
  displayOrientation,
  mainWrapperRef,
  answerInputPaneRef,
  stackedContainerRef,
  postSubmissionScrollPointRef,
  hideUsernameBar,
  className,
  focusQuestionOnMount,
  children
}: PracticeViewWrapperProps) => {
  const previousQuestionId = usePrevious(activeQuestion && activeQuestion.getId());
  const previousStudentId = usePrevious(studentId);
  const previousRenderType = usePrevious(questionRendererType);

  const {navBehavior} = useContext(GlobalNavBehaviorContext);

  useEffect(() => {
    callAction(appActions.SET_FOOTER_DISPLAY, false);
    copyToClipboard.preventCopy();
    return () => {
      callAction(appActions.SET_FOOTER_DISPLAY, true);
      copyToClipboard.allowCopy();
    };
  }, []);

  useEffect(() => {
    // allow copying for free response questions
    if (activeQuestion && activeQuestion.getQuestionType() === QUESTION_TYPES.FREE_RESPONSE.name) {
      copyToClipboard.allowCopy();
    } else {
      copyToClipboard.preventCopy();
    }
  }, [activeQuestion]);

  const updateScrollPosition = debounce(
    // oxlint-disable-next-line react/refs -- the ref is only read when the debounced/stable callback fires later, not during render
    useCallback(() => {
      if (!mainWrapperRef) {
        return;
      }
      // checking for changes to determine if we should trigger auto scroll behavior
      const isQuestionChanging = previousQuestionId !== (activeQuestion && activeQuestion.getId());
      const isStudentChanging = previousStudentId !== studentId;
      // we check if studentId exists or not because in ViewingStudentSubmission.tsx, a teacher can go from viewing a
      // student who didn't answer (PreGuess) to one who did which would trigger scroll behavior. By checking for if
      // studentId exists, we prevent getIsRendererTypeChanging from returning true which prevents scroll behavior
      const isRendererTypeChanging = previousRenderType !== questionRendererType && !studentId;

      const shouldScroll = isQuestionChanging || isStudentChanging || isRendererTypeChanging;
      if (!shouldScroll) {
        return;
      }

      // If we're going from one question to another, or if we're going from the PostGuess to the PreGuess state, we snap back to the top
      const isContentChanging = isQuestionChanging || isStudentChanging;
      const shouldScrollToTop = isContentChanging || questionRendererType === 'PreGuess';
      if (shouldScrollToTop) {
        // Get appropriate scroll container based on orientation
        const scrollContainerMap = {
          vertical: answerInputPaneRef?.current,
          horizontal: stackedContainerRef?.current,
          default: mainWrapperRef?.current
        };

        // Swapping the content snaps; returning to the top of the same question
        // mirrors the smooth ride that scrolled down to the solution.
        scrollContainerMap[displayOrientation]?.scrollTo({
          top: 0,
          behavior: isContentChanging ? 'auto' : 'smooth'
        });
      } else {
        // Otherwise, we're going from the PreGuess to the PostGuess state, so we scroll to the explanation
        postSubmissionScrollPointRef.current?.scrollIntoView({
          block: 'start',
          behavior: 'smooth'
        });
      }
    }, [
      previousQuestionId,
      previousStudentId,
      previousRenderType,
      questionRendererType,
      mainWrapperRef,
      activeQuestion,
      studentId,
      displayOrientation,
      answerInputPaneRef,
      stackedContainerRef,
      postSubmissionScrollPointRef
    ]),
    300
  );

  useEffect(() => {
    updateScrollPosition();
  }, [updateScrollPosition]);

  const activeQuestionId = activeQuestion?.getId();
  /* eslint-disable react-you-might-not-need-an-effect/no-event-handler -- imperative focus is a real side effect */
  useEffect(() => {
    const isNewQuestion = previousQuestionId
      ? previousQuestionId !== activeQuestionId
      : focusQuestionOnMount;
    if (activeQuestionId && isNewQuestion && mainWrapperRef?.current) {
      focusQuestionArea(mainWrapperRef.current);
    }
  }, [activeQuestionId, previousQuestionId, focusQuestionOnMount, mainWrapperRef]);
  /* eslint-enable react-you-might-not-need-an-effect/no-event-handler */

  return (
    <div
      className={cx('flex flex-col overflow-auto', className, {
        'practice-view-wrapper--without-global-nav':
          navBehavior === GlobalNavBehaviorEnum.NOT_VISIBLE && !hideUsernameBar,
        'practice-view-wrapper--without-global-nav-and-username-bar':
          navBehavior === GlobalNavBehaviorEnum.NOT_VISIBLE && hideUsernameBar,
        'practice-view-wrapper': navBehavior === GlobalNavBehaviorEnum.VISIBLE_STICKY
      })}
      data-testid='practice-view__container'
    >
      {children}
    </div>
  );
};
